/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { delay, http, HttpResponse } from 'msw';
import type {
  AdverseWeatherTAC,
  TafFromBackend,
  TafFromFrontEnd,
  TafType,
} from '../../types';
import { fakeTafConfig } from '../mockdata/fakeTafConfig';
import { fakeTafList } from '../mockdata/fakeTafList';
import { fakeNafConfig } from '../mockdata/naf/fakeNafConfig';
import { fakeNafList } from '../mockdata/naf/fakeNafList';

let fakeUpdatedTafList = [...fakeTafList];
const setFakeUpdatedTafList = (newList: TafFromBackend[]): void => {
  fakeUpdatedTafList = newList;
};
const getFakeUpdatedTafList = (): TafFromBackend[] => fakeUpdatedTafList;

let fakeUpdatedNafList = [...fakeNafList];
const setFakeUpdatedNafList = (newList: TafFromBackend[]): void => {
  fakeUpdatedNafList = newList;
};
const getFakeUpdatedNafList = (): TafFromBackend[] => fakeUpdatedNafList;

export const fakeTafTac =
  'TAF EHAM 0812/0918 20020G35KT 0500 SN BKN040 BECMG 0814/0816 0700 BKN001=';
export const fakeTafTacJson: AdverseWeatherTAC = {
  tac: [
    [
      { text: 'TAF' },
      { text: 'EHAM' },
      { text: '0812/0918' },
      { text: '20020G35KT', type: 'adverse' },
      { text: '0500', type: 'adverse' },
      { text: 'SN', type: 'adverse' },
      { text: 'BKN040' },
    ],
    [
      { text: 'BECMG' },
      { text: '0814/0816' },
      { text: '0700', type: 'adverse' },
      { text: 'BKN001', type: 'adverse' },
      { text: '=' },
    ],
  ],
};

export const tafEndPoints = [
  http.get(/^.*\/taflist$/, ({ request }) => {
    const url = new URL(request.url);
    if ((url.searchParams.get('type') as TafType) === 'NAF') {
      return HttpResponse.json(getFakeUpdatedNafList());
    }

    return HttpResponse.json(getFakeUpdatedTafList());
  }),
  http.post(/^.*\/taf$/, async () => {
    await delay();
    return HttpResponse.json(null);
  }),
  http.patch(/^.*\/taf$/, async ({ request }) => {
    const patchTaf = (await request.json()) as TafFromFrontEnd;

    const items =
      patchTaf.taf.type === 'NAF'
        ? getFakeUpdatedNafList()
        : getFakeUpdatedTafList();

    const updatedList = items.map((tafFromBackend) => {
      if (tafFromBackend.taf.uuid === patchTaf.taf.uuid) {
        return { ...tafFromBackend, editor: patchTaf.editor! };
      }
      return tafFromBackend;
    });

    if (patchTaf.taf.type === 'NAF') {
      setFakeUpdatedNafList(updatedList);
    } else {
      setFakeUpdatedTafList(updatedList);
    }

    await delay();
    return HttpResponse.json(null);
  }),
  http.post(/^.*\/taf2tac$/, async () => {
    await delay();
    return HttpResponse.json(fakeTafTacJson);
  }),
  http.get(/^.*\/taf-config/, ({ request }) => {
    const url = new URL(request.url);
    if ((url.searchParams.get('type') as TafType) === 'NAF') {
      return HttpResponse.json(fakeNafConfig);
    }
    return HttpResponse.json(fakeTafConfig);
  }),
];
